import { IssueStatus, TaxonomyFacet } from '@edgebox/sync-core-data-definitions';
import { ClientContentItemEntity, ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { BrandPresenceFigureCard } from './BrandPresenceFigureCard';
import { MAX_TAGS_SHOWN } from '../../Box/pageFigures';
import { brandPresencePageIssuesPath } from '../brandPresencePaths';
import { termDetailPath, termRouteReference } from '../GovernanceTaxonomyManagement.utils';
import { resolveTermFromIndex, TaxonomyTermNameBadge } from '../Onboarding/ContentInventory/Components/TaxonomyTermNameBadge';
import { useTaxonomyTermIndex } from '../Onboarding/ContentInventory/Components/taxonomyTermIndex';
import { facetScope } from '../taxonomyFacetMetadata';
import { FACETS_SHOWN_ON_CARDS, KPI_CARD_ROW_CLASS_NAME, toTaxonomyFacet } from './ContentItemKpiCards';
import { COUNT_ONLY, countLabel, countStateLabel } from './GovernanceTermUsageCards';
import { isDisplayableTerm } from './termStatusPresentation';

/**
 * The figures of a page that the cards above these do not carry: how many of its issues are still
 * open, and the tags it is classified by that no card above names.
 *
 * It belongs to the page's own screen inside the app and not to the card row above it, which is also
 * rendered in the box a CMS embeds beside its editor: everything here links into the app, and a link
 * that replaces a box inside someone's content-edit screen is a link that should not be there.
 */

/** One tag of the page, as much of it as this screen holds: what it is of, and what it is known by. */
interface IPageTag {
  facet?: TaxonomyFacet;
  key: string;
}

/**
 * The tags a page carries, in assignment order, one per facet and key.
 *
 * An assignment addresses its tag by a stable key and by the id of the tag's revision, and only the
 * first of the two says which tag it is: the shared index that names a tag is keyed by it, and so is
 * the tag's own page. An assignment carrying no key is therefore a tag this screen can neither name
 * nor open, and it is left out here exactly as the cards above leave it out.
 *
 * The facet is read through the reader the cards above read it with, so one assignment is the same
 * facet to both: a facet spelled differently by the store it came from cannot be a tag those cards
 * draw and a tag left over for this one.
 */
export function toPageTags(item: ClientContentItemEntity | undefined): IPageTag[] {
  const seen = new Set<string>();
  const tags: IPageTag[] = [];

  for (const assignment of item?.terms?.assigned || []) {
    const key = String(assignment?.term?.getStableKey?.() || '').trim();
    if (!key) {
      continue;
    }

    const facet = toTaxonomyFacet(assignment?.facet);
    const identity = `${facet || ''}:${key}`;
    if (seen.has(identity)) {
      continue;
    }

    seen.add(identity);
    tags.push({ facet, key });
  }

  return tags;
}

/**
 * The tags of a page that belong to the project's own vocabularies.
 *
 * A page carries two kinds of tag. The ones a team curates classify the page — its markets, its
 * programmes, the initiative it was written for — and are what a person means by a tag. The rest are
 * the vocabularies the platform manages for its own analysis: the features a health check evaluates,
 * the kinds an issue is filed under, the engines and locales an answer was read in. Those are read on
 * the screens that use them — the health tab of this very page reads the feature tags to build its
 * rows — and a card repeating them here would show the same analysis twice, in words nobody chose.
 *
 * Which vocabularies the platform manages is read from the one place this app declares it, so a
 * vocabulary that changes hands changes here with it. A facet that declaration does not name is
 * treated as the project's own, which is what a vocabulary an operator created is.
 */
export function toProjectTags(tags: IPageTag[]): IPageTag[] {
  return tags.filter((tag) => facetScope(tag.facet) !== 'system');
}

/**
 * The tags left for this card: the project's own, less the ones the cards above already name.
 *
 * The facets to leave out are read from the row above, which answers them from the selectors its
 * cards render with, so a facet those cards start drawing leaves this card the same day and a tag is
 * shown on exactly one card of the screen. A tag assigned under two facets is two tags on the screen,
 * as it is in the store, and reads as the two classifications it is.
 */
export function toOtherTags(tags: IPageTag[]): IPageTag[] {
  return toProjectTags(tags).filter((tag) => !tag.facet || !FACETS_SHOWN_ON_CARDS.has(tag.facet));
}

/**
 * One tag, opening the tag's own page.
 *
 * The link carries the reference every other link to a tag carries. A tag whose facet did not resolve
 * keeps its badge and loses only the link: its page is addressed by the facet, so there is no page to
 * open, and dropping the tag would hide a tag the page carries.
 */
function PageTagBadge({ api, tag }: { api?: ISyncCoreApi; tag: IPageTag }) {
  const badge = <TaxonomyTermNameBadge api={api} termKey={tag.key} termFacet={tag.facet} applyFacetPresentation />;
  const reference = termRouteReference({ key: tag.key });

  if (!tag.facet || !reference) {
    return badge;
  }

  return (
    <Link to={termDetailPath(tag.facet, reference)} className="no-underline">
      {badge}
    </Link>
  );
}

/**
 * How many issues of this page are still open, and the tags it carries that no card above names.
 */
export function ContentItemPageFigures({ api, item }: { api?: ISyncCoreApi; item?: ClientContentItemEntity }) {
  const [openIssueCount, setOpenIssueCount] = useState<number | 'failed' | undefined>(undefined);
  const contentItemKey = item?.key ? String(item.key) : undefined;

  /**
   * The count is asked of the list that holds the rows, at the smallest page that list answers: the
   * total sits in the envelope, so the screen pays for a number rather than for rows nobody reads.
   * The page's own record declares a rollup of the same figure and nothing writes it, so the list is
   * the only place this number exists.
   *
   * Only the open issues are counted, which is the status the card's link opens the list on, so the
   * number and the rows behind it are one set.
   */
  useEffect(() => {
    if (!api || !contentItemKey) {
      setOpenIssueCount(undefined);
      return;
    }

    let active = true;
    setOpenIssueCount(undefined);

    void (async () => {
      try {
        const response = await api.governance.issues.list(COUNT_ONLY, { contentItemKey, status: IssueStatus.Open });

        if (active) {
          setOpenIssueCount(response.totalNumberOfItems ?? 0);
        }
      } catch {
        // One number that cannot be read says so rather than reporting a zero, which would read as
        // "this page is clean".
        if (active) {
          setOpenIssueCount('failed');
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [api, contentItemKey]);

  const { index } = useTaxonomyTermIndex(api);
  const otherTags = useMemo(() => toOtherTags(toPageTags(item)), [item]);
  // An archived tag is not part of what the customer is looking at, so the badge draws nothing for
  // one. It is dropped here rather than rendered as an empty chip, which is also what lets the card
  // say the page has no other tags when every one of them is archived.
  const displayableTags = otherTags.filter((tag) => isDisplayableTerm(resolveTermFromIndex(index, tag.key)?.status));
  // The bound is the one the box a site embeds for the same page draws by, so one page's tags stop at
  // the same place on both surfaces, and what it leaves out is counted under the chips rather than
  // dropped in silence.
  const shownTags = displayableTags.slice(0, MAX_TAGS_SHOWN);
  const hiddenTagCount = displayableTags.length - shownTags.length;

  const issuesCard = (
    <BrandPresenceFigureCard title="Open issues" className="h-full">
      <span className="text-[2rem] leading-none font-bold tracking-tight text-slate-900 tabular-nums">{countLabel(openIssueCount)}</span>
      {/* A number that could not be read says so in words under the mark that stands for it: "we could
          not count this" is the one state a reader must not be left to infer from punctuation. */}
      {openIssueCount === 'failed' ? <p className="text-xs text-slate-500 mt-1 mb-0">Could not be counted</p> : null}
      {/* And in the card's own text for a reader who is listening, in the words the cards counting a
          tag's uses use: "…" and "—" are marks a screen reader has nothing to say about. It is read
          where the reader is rather than announced: the FAQ tab below keeps a live region of its own,
          and a second one standing above the tabs is the first one a reader — and that tab's own
          specs — find on the screen, so what the tab says arrives from the wrong place. */}
      <span className="sr-only">{countStateLabel(openIssueCount)}</span>
    </BrandPresenceFigureCard>
  );

  return (
    // The grid of the card row this one continues, read from that row rather than spelled again here,
    // so each card stands under a card above it in the same gutters for as long as that row does. The
    // link-and-card pair is the one every card that is a link on these screens is built from
    // (`GovernanceTermUsageCards.tsx`): the card fills its column, so the whole card is the link.
    <div className={KPI_CARD_ROW_CLASS_NAME}>
      <div className="col">
        {contentItemKey ? (
          <Link
            to={brandPresencePageIssuesPath({ contentItemKey, status: IssueStatus.Open })}
            className="group/figure block h-full rounded-2xl no-underline transition-transform duration-200 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            {issuesCard}
          </Link>
        ) : (
          <div className="h-full">{issuesCard}</div>
        )}
      </div>

      <div className="col">
        <BrandPresenceFigureCard title="Other tags" className="h-full">
          {shownTags.length ? (
            <>
              <div className="flex flex-wrap gap-1.5">
                {shownTags.map((tag) => (
                  <PageTagBadge key={`${tag.facet || 'unknown'}:${tag.key}`} api={api} tag={tag} />
                ))}
              </div>
              {/* What the bound left out, in the words the box uses for it, so a page carrying more
                  tags than fit says so rather than looking like a page carrying twenty. */}
              {hiddenTagCount > 0 ? <p className="text-xs text-slate-500 mt-2 mb-0">and {hiddenTagCount} more</p> : null}
            </>
          ) : (
            <div className="text-sm text-slate-500">No other tags assigned.</div>
          )}
        </BrandPresenceFigureCard>
      </div>
    </div>
  );
}
